// ImportDialog — the ad-hoc import wizard (right-click a table → "Import data…")
// AND the admin's "Save as reusable import" authoring surface. Steps: pick a
// file + source options → Analyze → per target column choose input (from a file
// header) / prefill (from a scalar SQL query) / skip, with a preview → INSERT vs
// upsert + destination → run (background job) or generate INSERTs.
//
// Running a SAVED definition is NOT this component — that's ImportRunner.jsx
// (stripped down: file + validate + import). This dialog always builds the
// `columns` spec the backend executor / saved-config use.
//
// Props: connectionId, database, table, onOpenInEditor(database, sql)?, onSaved()?, onClose()
import { createSignal, createMemo, onMount, Show, For } from 'solid-js';
import Modal from './Modal.jsx';
import ProgressModal from './ProgressModal.jsx';
import GeneratedSqlModal from './GeneratedSqlModal.jsx';
import CodeMirrorSQL from './CodeMirrorSQL.jsx';
import DateFormatPicker from './DateFormatPicker.jsx';
import SPDebugger from './SPDebugger.jsx';
import { isDateType, looksLikeDate } from '../lib/dateformats.js';
import { currentUser } from '../lib/auth-store.js';
import { analyzeImport, reanalyzeImport, runImport, resolveImport, getImportColumns } from '../lib/import.js';
import { createImportDef, updateImportDef } from '../lib/importdefs.js';
import { toast } from '../lib/toast.js';
import { sqlSchemaFor } from '../lib/schema.js';

const DELIMS = [
  { label: 'Auto-detect', v: '' },
  { label: 'Comma  ,', v: ',' },
  { label: 'Semicolon  ;', v: ';' },
  { label: 'Tab', v: '\t' },
];

// parseSingleCall extracts `db`.`proc` from a per-row statement when it's a
// SINGLE `CALL proc(...)` — the only shape we can hand to the SP debugger. Any
// extra statement (a `;` separating more SQL) or a non-CALL returns null, which
// disables the Debug button.
export function parseSingleCall(sql) {
  let s = String(sql || '').trim().replace(/;\s*$/, '');
  if (s.includes(';')) return null; // more than one statement
  const m = s.match(/^CALL\s+(?:`?([A-Za-z0-9_]+)`?\s*\.\s*)?`?([A-Za-z0-9_]+)`?\s*\(/i);
  if (!m) return null;
  return { db: m[1] || null, proc: m[2] };
}

// forced reports whether a target column must be supplied (input or prefill):
// a non-auto-increment PK, or a NOT NULL column with no default.
export function isForcedColumn(t) {
  if (t.generated || t.autoIncrement) return false;
  if (t.isPrimary) return true;
  return !t.nullable && !t.hasDefault;
}

export default function ImportDialog(props) {
  const isAdmin = () => currentUser()?.role === 'admin';
  const sqlSchema = sqlSchemaFor(() => props.connectionId, () => props.database); // editor autocomplete

  // When editing a saved definition, pre-fill everything from its config.
  const def = props.definition || null;
  const savedColumns = def?.columns || null;
  const o = def?.options || {};
  const editing = () => !!props.editId;

  const [file, setFile] = createSignal(null);
  const [hasHeader, setHasHeader] = createSignal(o.hasHeader !== false);
  const [delimiter, setDelimiter] = createSignal(o.delimiter || '');
  const [encoding, setEncoding] = createSignal(o.encoding || 'utf8');
  const [sheet, setSheet] = createSignal(o.sheet || '');
  const [nullToken, setNullToken] = createSignal(def?.nullToken || '');
  const [emptyAsEmpty, setEmptyAsEmpty] = createSignal(!!def?.emptyAsEmpty);
  const [strategy, setStrategy] = createSignal(def?.strategy || 'insert');
  const [destination, setDestination] = createSignal(def?.destination || 'execute');
  const [onError, setOnError] = createSignal(def?.onError || 'skip'); // 'skip' (auto-skip + report) | 'ask' (pause)
  const [rowSql, setRowSql] = createSignal(def?.rowSql || ''); // destination 'sql': the per-row statement(s)
  const [debugImport, setDebugImport] = createSignal(false);
  // The procedure to debug, when the per-row SQL is a single CALL.
  const callTarget = createMemo(() => (destination() === 'sql' ? parseSingleCall(rowSql()) : null));
  const [dateFormats, setDateFormats] = createSignal({ ...(def?.dateFormats || {}) }); // file header → date format key
  const setDF = (header, key) => setDateFormats((m) => {
    const n = { ...m };
    if (key) n[header] = key; else delete n[header];
    return n;
  });

  const [token, setToken] = createSignal(null);
  const [analysis, setAnalysis] = createSignal(null);
  const [targets, setTargets] = createSignal([]);
  const [colState, setColState] = createSignal({}); // target → { mode, source, query }
  const [busy, setBusy] = createSignal(false);
  const [uploadPct, setUploadPct] = createSignal(null);
  const [jobToken, setJobToken] = createSignal(null);
  const [generatedSql, setGeneratedSql] = createSignal(null);

  const [saveMode, setSaveMode] = createSignal(false);
  const [saveName, setSaveName] = createSignal(props.editName || '');
  const [saveDesc, setSaveDesc] = createSignal(props.editDesc || '');
  const [prefilled, setPrefilled] = createSignal(false); // edit mode: config loaded without a file

  // Edit mode: load target columns server-side (no file) and seed the mapping
  // from the saved config, so the editor renders without an upload. A file stays
  // optional — uploading + Refresh re-reads headers and re-applies the mapping.
  onMount(async () => {
    if (!editing()) return;
    try {
      const r = await getImportColumns(props.connectionId, props.database, props.table);
      setTargets(r.targetColumns || []);
      setColState(initColState(r.targetColumns || [], null));
      setPrefilled(true);
    } catch (e) {
      toast.error('Load columns: ' + (e?.message || e));
    }
  });

  const opts = () => ({ hasHeader: hasHeader(), delimiter: delimiter(), encoding: encoding(), sheet: sheet() });
  const srcOptions = () => ({ format: analysis()?.format, ...opts() });
  const sheets = () => analysis()?.sheets || [];
  // Headers come from the uploaded file, or (editing without a file) from the
  // saved config — its sourceHeaders, else reconstructed from saved sources +
  // date-format keys (so legacy defs degrade gracefully).
  const storedHeaders = () => {
    if (def?.sourceHeaders?.length) return def.sourceHeaders;
    const set = new Set();
    for (const c of (savedColumns || [])) if (c.source) set.add(c.source);
    for (const k of Object.keys(def?.dateFormats || {})) set.add(k);
    return [...set];
  };
  const headers = () => analysis()?.headers || storedHeaders();
  const previewRows = () => analysis()?.previewRows || (def?.sampleRow ? [def.sampleRow] : []);
  const isXlsx = () => analysis()?.format === 'xlsx';
  const drift = () => { const a = analysis(); return a && a.hasHeader && a.rowsWide > a.headers.length; };

  // First non-empty sample value for a header (for the date preview).
  const sampleFor = (header) => {
    const i = headers().indexOf(header);
    if (i < 0) return '';
    for (const row of previewRows()) {
      if (row[i] != null && String(row[i]).trim() !== '') return row[i];
    }
    return '';
  };
  // Likely date columns: headers mapped to a DATE/DATETIME target (INSERT modes),
  // whose samples look date-like, or already given a format.
  const dateColumns = () => {
    const heads = headers();
    if (!heads.length) return [];
    const dateTargetHeaders = new Set();
    if (destination() !== 'sql') {
      for (const t of targets()) {
        const s = colState()[t.name];
        if (s && (s.mode === 'input' || s.mode === 'enum') && s.source && isDateType(t.type)) dateTargetHeaders.add(s.source);
      }
    }
    return heads.map((h) => {
      const sample = sampleFor(h);
      return { header: h, sample, candidate: dateTargetHeaders.has(h) || looksLikeDate(sample) || !!dateFormats()[h] };
    });
  };

  // Seed per-column state. When editing a saved definition, reapply its saved
  // modes/sources/queries (source matched to the current file's headers by name,
  // best-effort). Otherwise seed from the auto-map: a name match → input; a
  // forced column with no match → input (empty, needs attention); else skip.
  // Generated/binary columns are always skip (locked, excluded).
  const initColState = (cols, autoMap) => {
    const am = Object.fromEntries((autoMap || []).map((m) => [m.target, m.source]));
    const saved = Object.fromEntries((savedColumns || []).map((c) => [c.target, c]));
    const byLower = new Map((headers() || []).map((h) => [String(h).toLowerCase(), h]));
    const st = {};
    for (const t of cols) {
      if (t.generated || t.binary) { st[t.name] = { mode: 'skip', source: '', query: '' }; continue; }
      const sc = saved[t.name];
      if (sc) {
        const src = sc.source ? (byLower.get(String(sc.source).toLowerCase()) || '') : '';
        st[t.name] = { mode: sc.mode || 'input', source: src, query: sc.prefillQuery || '' };
        continue;
      }
      const src = am[t.name] || '';
      if (src) st[t.name] = { mode: 'input', source: src, query: '' };
      else st[t.name] = { mode: isForcedColumn(t) ? 'input' : 'skip', source: '', query: '' };
    }
    return st;
  };

  const patch = (target, partial) =>
    setColState((s) => ({ ...s, [target]: { ...s[target], ...partial } }));

  const mappedCount = createMemo(() =>
    Object.values(colState()).filter((s) => s.mode === 'input' || s.mode === 'prefill' || s.mode === 'enum').length);
  // Rows still needing attention: input/enum with no source, or prefill/enum with no query.
  const incomplete = createMemo(() =>
    targets().filter((t) => {
      const s = colState()[t.name];
      if (!s) return false;
      if (s.mode === 'input') return !s.source;
      if (s.mode === 'prefill') return !s.query.trim();
      if (s.mode === 'enum') return !s.source || !s.query.trim();
      return false;
    }).map((t) => t.name));

  const buildColumns = () =>
    targets().map((t) => {
      const s = colState()[t.name] || { mode: 'skip' };
      return { target: t.name, mode: s.mode, source: s.source || '', prefillQuery: s.query || '' };
    });

  // titleCaseVar mirrors the backend's titleCase (forms.go): split on _/-/space,
  // uppercase the first letter of each part, lowercase the rest. So the @v names
  // shown here match exactly what the executor binds (KodeGol → @vKodegol).
  const titleCaseVar = (s) => {
    const parts = String(s).split(/[_\-\s]+/).filter(Boolean);
    if (!parts.length) return s;
    return parts.map((p) => p[0].toUpperCase() + p.slice(1).toLowerCase()).join('');
  };
  const availableVars = () => headers().map((h) => '@v' + titleCaseVar(h));
  // Per-row prefill queries bind each INPUT column as @v<TitleCase(target)>.
  const inputVars = () => targets()
    .filter((t) => { const s = colState()[t.name]; return s && s.mode === 'input' && s.source; })
    .map((t) => '@v' + titleCaseVar(t.name));
  // The run/save gate: SQL mode needs a statement; the INSERT modes need a mapping.
  const canRun = () => (destination() === 'sql' ? !!rowSql().trim() : mappedCount() > 0 && incomplete().length === 0);
  // The config editor renders once we have a schema: from an uploaded file, or
  // (edit mode) from the saved config loaded server-side. Saving a definition is
  // allowed in that prefilled state; RUNNING still needs an actual file (token).
  const editorReady = () => (!!token() && !!analysis()) || prefilled();
  const canSaveCfg = () => !!token() || (editing() && prefilled());

  const doAnalyze = async () => {
    if (!file()) return;
    setBusy(true); setUploadPct(0);
    try {
      const r = await analyzeImport(props.connectionId, props.database, props.table, file(), opts(),
        (loaded, total) => setUploadPct(total ? Math.round((loaded / total) * 100) : 0));
      setToken(r.token); setAnalysis(r.analysis); setTargets(r.targetColumns || []);
      setColState(initColState(r.targetColumns || [], r.autoMap));
    } catch (e) {
      toast.error('Analyze failed: ' + (e?.message || e));
    } finally { setBusy(false); setUploadPct(null); }
  };

  const reanalyze = async () => {
    if (!token()) return;
    setBusy(true);
    try {
      const r = await reanalyzeImport(token(), opts());
      setAnalysis(r.analysis);
      setColState(initColState(targets(), r.autoMap));
    } catch (e) {
      toast.error('Re-analyze failed: ' + (e?.message || e));
    } finally { setBusy(false); }
  };

  const setOpt = (setter) => (v) => { setter(v); reanalyze(); };

  const currentConfig = () => ({
    options: srcOptions(), columns: buildColumns(), strategy: strategy(),
    nullToken: nullToken(), emptyAsEmpty: emptyAsEmpty(), destination: destination(),
    onError: onError(), rowSql: rowSql(), dateFormats: dateFormats(),
    // Captured so a future Edit can render without re-uploading a file.
    sourceHeaders: headers(), sampleRow: analysis()?.previewRows?.[0] || def?.sampleRow || null,
  });

  const buildRunBody = (mode) => ({
    mode, options: srcOptions(), columns: buildColumns(), strategy: strategy(),
    nullToken: nullToken(), emptyAsEmpty: emptyAsEmpty(), onError: onError(),
    rowSql: rowSql(), dateFormats: dateFormats(), fileName: file()?.name || '',
  });

  const run = async () => {
    if (!token() || !canRun()) return;
    setBusy(true);
    try {
      const r = await runImport(token(), buildRunBody(destination()));
      if (destination() === 'generate') {
        if (r.truncated) toast.info(`Generated ${r.rows} rows (capped). Use “Run into table” for the whole file.`);
        else toast.success(`Generated INSERTs for ${r.rows} row(s).`);
        if (props.onOpenInEditor) { props.onOpenInEditor(props.database, r.sql || ''); props.onClose?.(); }
        else setGeneratedSql(r.sql || '');
      } else {
        setJobToken(r.token);
      }
    } catch (e) {
      toast.error('Import failed: ' + (e?.message || e));
    } finally { setBusy(false); }
  };

  // Retry a failed/aborted run. The temp file is deleted after each run, so we
  // re-upload the retained File for a fresh token — but capture the current run
  // body FIRST (re-analyzing would reset the column mapping), and re-run with it.
  const retry = async () => {
    if (!file()) { toast.error('No file to retry — re-select the file.'); return; }
    const body = buildRunBody('execute'); // preserve the current mapping/options
    setBusy(true); setJobToken(null);
    try {
      const a = await analyzeImport(props.connectionId, props.database, props.table, file(), opts());
      setToken(a.token);
      const r = await runImport(a.token, body);
      setJobToken(r.token);
    } catch (e) {
      // Re-upload commonly fails because the file was EDITED on disk since you
      // picked it (the browser won't re-read a changed selection). Drop back to the
      // form so you can re-select the updated file.
      setToken(null); setAnalysis(null);
      toast.info('Re-select your file to import the updated version.' + (e?.message ? ` (${e.message})` : ''));
    } finally { setBusy(false); }
  };

  const saveReusable = async () => {
    if (!saveName().trim()) { toast.error('Give the import a name.'); return; }
    if (!editing() && !token()) { toast.error('Analyze a sample file first so the config is captured.'); return; }
    if (destination() === 'sql' && !rowSql().trim()) { toast.error('Write the per-row SQL first.'); return; }
    if (destination() !== 'sql' && incomplete().length) { toast.error('Finish mapping every input/prefill/enum column first.'); return; }
    setBusy(true);
    try {
      const config = JSON.stringify(currentConfig());
      if (editing()) {
        await updateImportDef(props.editId, { name: saveName().trim(), description: saveDesc().trim(), config });
        toast.success('Import updated.');
      } else {
        await createImportDef({
          name: saveName().trim(), description: saveDesc().trim(),
          connectionId: props.connectionId, databaseName: props.database, tableName: props.table, config,
        });
        toast.success(`Saved reusable import “${saveName().trim()}”.`);
      }
      setSaveMode(false); props.onSaved?.();
    } catch (e) {
      toast.error('Save failed: ' + (e?.message || e));
    } finally { setBusy(false); }
  };

  return (
    <Show when={!jobToken()} fallback={
      <ProgressModal token={jobToken()} title={`Importing into — ${props.database}.${props.table}`}
        kind="import" onResolve={(action) => resolveImport(jobToken(), action)} onRetry={retry} onClose={props.onClose} />
    }>
      <Show when={!generatedSql()} fallback={<GeneratedSqlModal sql={generatedSql()} onClose={props.onClose} />}>
        <Modal open={true} title={editing() ? `Edit import — ${props.editName || props.table}` : `Import data into ${props.table}`} onClose={props.onClose} wide={editorReady()}
               closeOnBackdrop={false} closeOnEscape={false}>
          <div class="import-dialog">
            <Show when={editing()}>
              <div class="import-warn import-warn-soft">
                Editing <span class="mono">{props.database}.{props.table}</span> — the saved mapping is loaded below.
                Uploading a file is <strong>optional</strong>; use it only to refresh the column list, then “Save changes”.
              </div>
            </Show>
            {/* ── Source ── */}
            <label class="restore-field">
              <span class="restore-field-label">File (.csv or .xlsx)</span>
              <input type="file" accept=".csv,.tsv,.xlsx" disabled={busy()}
                     onChange={(e) => { setFile(e.currentTarget.files?.[0] || null); setToken(null); setAnalysis(null); }} />
            </label>

            <div class="import-opts">
              <label class="import-opt">
                <input type="checkbox" checked={hasHeader()} disabled={busy()}
                       onChange={(e) => setOpt(setHasHeader)(e.currentTarget.checked)} />
                <span>First row is a header</span>
              </label>
              <Show when={!isXlsx()}>
                <label class="import-opt"><span class="muted">Delimiter</span>
                  <select class="input" disabled={busy()} value={delimiter()}
                          onChange={(e) => setOpt(setDelimiter)(e.currentTarget.value)}>
                    <For each={DELIMS}>{(d) => <option value={d.v} selected={d.v === delimiter()}>{d.label}</option>}</For>
                  </select>
                </label>
                <label class="import-opt"><span class="muted">Encoding</span>
                  <select class="input" disabled={busy()} value={encoding()}
                          onChange={(e) => setOpt(setEncoding)(e.currentTarget.value)}>
                    <option value="utf8" selected={encoding() === 'utf8'}>UTF-8</option>
                    <option value="latin1" selected={encoding() === 'latin1'}>Windows-1252 / Latin-1</option>
                  </select>
                </label>
              </Show>
              <Show when={isXlsx() && sheets().length > 1}>
                <label class="import-opt"><span class="muted">Sheet</span>
                  <select class="input" disabled={busy()} value={sheet()}
                          onChange={(e) => setOpt(setSheet)(e.currentTarget.value)}>
                    <For each={sheets()}>{(s) => <option value={s} selected={s === sheet()}>{s}</option>}</For>
                  </select>
                </label>
              </Show>
            </div>

            {/* Create mode: must analyze a file to proceed. */}
            <Show when={!editing() && !token()}>
              <div class="restore-actions">
                <button class="btn btn-ghost" onClick={props.onClose} disabled={busy()}>Cancel</button>
                <button class="btn btn-primary" onClick={doAnalyze} disabled={!file() || busy()}>Analyze file →</button>
              </div>
            </Show>
            {/* Edit mode: the file is optional — refresh the columns from it. */}
            <Show when={editing()}>
              <div class="restore-actions">
                <button class="btn btn-ghost btn-sm" onClick={doAnalyze} disabled={!file() || busy()}>↻ Refresh columns from file</button>
              </div>
            </Show>
            <Show when={uploadPct() !== null}>
              <div class="progress-track"><div class="progress-fill" style={`width:${uploadPct()}%`} /></div>
              <div class="muted" style="font-size:12px;">Uploading… {uploadPct()}%</div>
            </Show>

            <Show when={editing() && !editorReady()}>
              <div class="muted" style="font-size:12px;margin-top:6px;">Loading saved configuration…</div>
            </Show>

            {/* ── Map columns ── */}
            <Show when={editorReady()}>
              <Show when={drift()}>
                <div class="import-warn">Some rows have more columns ({analysis().rowsWide}) than the header ({headers().length}) — extra cells are ignored.</div>
              </Show>

              <Show when={destination() !== 'sql'}>
              <div class="import-section-title">Map columns <span class="muted">({mappedCount()} mapped · <code>*</code> = required)</span></div>
              <div class="import-map">
                <For each={targets()}>
                  {(t) => {
                    const locked = t.generated || t.binary;
                    const st = () => colState()[t.name] || { mode: 'skip', source: '', query: '' };
                    const forced = isForcedColumn(t);
                    return (
                      <div class="import-map-row import-map-row-modal">
                        <div class="import-map-target">
                          <span class="mono">{t.name}{forced ? ' *' : ''}</span>
                          <span class="muted import-map-type">{t.type}</span>
                          <Show when={t.isPrimary}><span class="import-badge">PK</span></Show>
                          <Show when={t.autoIncrement}><span class="import-badge">AI</span></Show>
                          <Show when={t.generated}><span class="import-badge">generated</span></Show>
                          <Show when={t.binary}><span class="import-badge">binary</span></Show>
                        </div>
                        <select class="input import-map-mode" disabled={locked}
                                value={locked ? 'skip' : st().mode}
                                onChange={(e) => patch(t.name, { mode: e.currentTarget.value })}>
                          <Show when={locked} fallback={<>
                            <option value="input" selected={st().mode === 'input'}>From file</option>
                            <option value="enum" selected={st().mode === 'enum'}>From file (allowed values)</option>
                            <option value="prefill" selected={st().mode === 'prefill'}>SQL value</option>
                            <Show when={!forced}><option value="skip" selected={st().mode === 'skip'}>Skip</option></Show>
                          </>}>
                            <option value="skip">Excluded</option>
                          </Show>
                        </select>
                        <Show when={!locked && st().mode === 'input'}>
                          <select class="input import-map-src" value={st().source}
                                  onChange={(e) => patch(t.name, { source: e.currentTarget.value })}>
                            <option value="">— choose header —</option>
                            <For each={headers()}>{(h) => <option value={h} selected={h === st().source}>{h}</option>}</For>
                          </select>
                        </Show>
                        <Show when={!locked && st().mode === 'prefill'}>
                          <div class="import-map-query">
                            <CodeMirrorSQL value={st().query} onInput={(v) => patch(t.name, { query: v })} schema={sqlSchema} height="52px"
                                           sessionVars={() => inputVars()}
                                           placeholder="SELECT … (one value; use @vColumn for input cells)" />
                          </div>
                        </Show>
                        <Show when={!locked && st().mode === 'enum'}>
                          <div class="import-map-enum">
                            <select class="input import-map-src" value={st().source}
                                    onChange={(e) => patch(t.name, { source: e.currentTarget.value })}>
                              <option value="">— choose header —</option>
                              <For each={headers()}>{(h) => <option value={h} selected={h === st().source}>{h}</option>}</For>
                            </select>
                            <div class="import-map-query">
                              <CodeMirrorSQL value={st().query} onInput={(v) => patch(t.name, { query: v })} schema={sqlSchema} height="52px"
                                             placeholder="SELECT allowed values, e.g. SELECT 'Yes' UNION ALL SELECT 'No'" />
                            </div>
                          </div>
                        </Show>
                      </div>
                    );
                  }}
                </For>
              </div>
              </Show>

              <div class="import-nulls">
                <label class="import-opt"><span class="muted">NULL token</span>
                  <input class="input" style="width:100px;" placeholder="(none)" value={nullToken()}
                         onInput={(e) => setNullToken(e.currentTarget.value)} />
                </label>
                <label class="import-opt">
                  <input type="checkbox" checked={emptyAsEmpty()} onChange={(e) => setEmptyAsEmpty(e.currentTarget.checked)} />
                  <span>Treat empty cells as empty string (default: NULL)</span>
                </label>
              </div>

              <Show when={previewRows().length}>
                <div class="import-section-title">Preview <span class="muted">(first {previewRows().length} row(s))</span></div>
                <div class="import-preview-wrap">
                  <table class="import-preview">
                    <thead><tr><For each={headers()}>{(h) => <th>{h}</th>}</For></tr></thead>
                    <tbody><For each={previewRows()}>
                      {(row) => <tr><For each={headers()}>{(_h, i) => <td>{row[i()] ?? ''}</td>}</For></tr>}
                    </For></tbody>
                  </table>
                </div>
              </Show>

              <DateFormatPicker columns={dateColumns()} value={dateFormats()} onChange={setDF} />

              <Show when={destination() !== 'sql'}>
                <div class="import-section-title">On duplicate key</div>
                <div class="import-radios">
                  <label class="import-radio">
                    <input type="radio" name="impstrat" checked={strategy() === 'insert'} onChange={() => setStrategy('insert')} />
                    <span>Plain INSERT <span class="muted">(error on duplicate)</span></span>
                  </label>
                  <label class="import-radio">
                    <input type="radio" name="impstrat" checked={strategy() === 'upsert'} onChange={() => setStrategy('upsert')} />
                    <span>ON DUPLICATE KEY UPDATE <span class="muted">(update mapped non-key columns)</span></span>
                  </label>
                </div>
              </Show>

              <div class="import-section-title">Destination</div>
              <div class="import-radios">
                <label class="import-radio">
                  <input type="radio" name="impdest" checked={destination() === 'execute'} onChange={() => setDestination('execute')} />
                  <span>Run into the table <span class="muted">(streamed, pause-on-error)</span></span>
                </label>
                <label class="import-radio">
                  <input type="radio" name="impdest" checked={destination() === 'generate'} onChange={() => setDestination('generate')} />
                  <span>Open INSERTs in a query editor</span>
                </label>
                <label class="import-radio">
                  <input type="radio" name="impdest" checked={destination() === 'sql'} onChange={() => setDestination('sql')} />
                  <span>Run a SQL statement per row <span class="muted">(bind each column as @v…)</span></span>
                </label>
              </div>
              <Show when={destination() === 'generate'}>
                <div class="import-warn import-warn-soft">The editor mode is capped at 5,000 rows. For bigger files use “Run into the table”.</div>
              </Show>

              {/* Per-row SQL: bind every file column as @v<TitleCase(header)>, then run this. */}
              <Show when={destination() === 'sql'}>
                <div class="import-section-title">SQL to run per row</div>
                <div class="import-warn import-warn-soft">
                  Each row's columns are bound as:{' '}
                  <span class="mono">{availableVars().join(', ')}</span>. The statement(s) below run once per row.
                </div>
                <div class="import-map-query" style="min-height:140px;">
                  <CodeMirrorSQL value={rowSql()} onInput={setRowSql} height="160px" schema={sqlSchema}
                                 sessionVars={() => availableVars()}
                                 placeholder={`e.g. CALL upsert_golongan(${(availableVars()[0] || '@vCol')}, …)`} />
                </div>
                <div style="margin-top:8px;">
                  <button class="btn btn-sm btn-ghost"
                          disabled={!callTarget()}
                          title={callTarget()
                            ? 'Step through the called procedure with a sample row’s @v… variables preset and watched'
                            : 'Debugging works when the per-row SQL is a single CALL to a procedure'}
                          onClick={() => setDebugImport(true)}>
                    🐞 Debug procedure
                  </button>
                </div>
              </Show>

              <Show when={destination() !== 'generate'}>
                <div class="import-section-title">On a bad row</div>
                <div class="import-radios">
                  <label class="import-radio">
                    <input type="radio" name="imperr" checked={onError() === 'skip'} onChange={() => setOnError('skip')} />
                    <span>Skip &amp; report <span class="muted">(keep going; download a CSV of rejected rows after)</span></span>
                  </label>
                  <label class="import-radio">
                    <input type="radio" name="imperr" checked={onError() === 'ask'} onChange={() => setOnError('ask')} />
                    <span>Ask each time <span class="muted">(pause on each error: skip / skip-all / abort)</span></span>
                  </label>
                </div>
              </Show>

              <Show when={destination() !== 'sql' && incomplete().length}>
                <div class="import-warn import-warn-soft">Finish these columns: {incomplete().join(', ')}.</div>
              </Show>

              {/* Save as reusable (create) / Save changes (edit) — admin only */}
              <Show when={isAdmin()}>
                <Show when={!editing() && !saveMode()}>
                  <button class="btn btn-sm btn-ghost" onClick={() => { setSaveName(''); setSaveDesc(''); setSaveMode(true); }}>
                    💾 Save as reusable import…
                  </button>
                </Show>
                <Show when={editing() || saveMode()}>
                  <div class="import-save-panel">
                    <input class="input" placeholder="Import name" value={saveName()} onInput={(e) => setSaveName(e.currentTarget.value)} />
                    <input class="input" placeholder="Description (optional)" value={saveDesc()} onInput={(e) => setSaveDesc(e.currentTarget.value)} />
                    <button class="btn btn-sm" onClick={saveReusable} disabled={busy() || !saveName().trim() || !canSaveCfg() || !canRun()}>
                      {editing() ? 'Save changes' : 'Save'}
                    </button>
                    <Show when={!editing()}>
                      <button class="btn btn-sm btn-ghost" onClick={() => setSaveMode(false)}>Cancel</button>
                    </Show>
                  </div>
                </Show>
              </Show>

              <div class="restore-actions">
                <button class="btn btn-ghost" onClick={props.onClose} disabled={busy()}>Cancel</button>
                <button class="btn btn-primary" onClick={run} disabled={busy() || !canRun() || !token()}
                        title={!token() ? 'Upload + refresh a file to run' : ''}>
                  {destination() === 'generate' ? 'Generate INSERTs'
                    : destination() === 'sql' ? '⚙ Run SQL per row' : '⬆ Import into table'}
                </button>
              </div>
            </Show>
          </div>

          {/* Debug the called procedure with one sample row's @v… variables */}
          <Show when={debugImport() && callTarget()}>
            <SPDebugger
              connectionId={props.connectionId}
              database={callTarget().db || props.database}
              routine={callTarget().proc}
              source=""
              sessionVarDefs={availableVars().map((name) => ({ name, default: '' }))}
              onClose={() => setDebugImport(false)}
            />
          </Show>
        </Modal>
      </Show>
    </Show>
  );
}
